import { personIcon } from './icons';
import { t } from './i18n';

const ROOT_CLASS = 'flix-chat-widget__name-required';
const MAX_NAME_LENGTH = 100;

export interface NameRequiredPanel {
  element: HTMLElement;
  setError(message: string | null): void;
  setSubmitting(isSubmitting: boolean): void;
  focus(): void;
}

/**
 * Rendered inside the panel body in place of the message list/composer
 * exactly once per customer: only ever shown after the bootstrap route
 * returns 428 (see widget.ts's connectToConversation — Shopify had no name
 * on file and none was stored yet). Submitting calls onSubmit(name); the
 * caller owns the actual save request and retry, this component only owns
 * its own form state (error text, disabled-while-submitting).
 */
export function createNameRequiredPanel(onSubmit: (name: string) => void): NameRequiredPanel {
  const container = document.createElement('div');
  container.className = ROOT_CLASS;

  const icon = document.createElement('div');
  icon.className = `${ROOT_CLASS}-icon`;
  icon.append(personIcon());

  const title = document.createElement('h2');
  title.className = `${ROOT_CLASS}-title`;
  title.textContent = t('nameRequiredTitle');

  const description = document.createElement('p');
  description.className = `${ROOT_CLASS}-description`;
  description.textContent = t('nameRequiredDescription');

  const form = document.createElement('form');
  form.className = `${ROOT_CLASS}-form`;

  const input = document.createElement('input');
  input.type = 'text';
  input.className = `${ROOT_CLASS}-input`;
  input.placeholder = t('nameInputPlaceholder');
  input.setAttribute('aria-label', t('nameInputAria'));
  input.autocomplete = 'name';
  input.maxLength = MAX_NAME_LENGTH;

  const errorText = document.createElement('p');
  errorText.className = `${ROOT_CLASS}-error`;
  errorText.hidden = true;

  const submitButton = document.createElement('button');
  submitButton.type = 'submit';
  submitButton.className = `${ROOT_CLASS}-submit`;
  submitButton.textContent = t('nameSaveAction');

  form.append(input, errorText, submitButton);
  container.append(icon, title, description, form);

  function setError(message: string | null): void {
    errorText.textContent = message ?? '';
    errorText.hidden = message === null;
  }

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const name = input.value.trim();

    if (name.length === 0) {
      setError(t('nameRequiredError'));
      input.focus();
      return;
    }

    setError(null);
    onSubmit(name);
  });

  return {
    element: container,
    setError,

    setSubmitting(isSubmitting: boolean): void {
      input.disabled = isSubmitting;
      submitButton.disabled = isSubmitting;
    },

    focus(): void {
      input.focus();
    },
  };
}
